/**
 * Handover of the microphone between the idle visualizer stream and a capture.
 *
 * The idle stream is decorative: it opens the selected input purely so the wav
 * bars move while nothing is being captured. A capture must take that mic over,
 * and hand it back when it ends — otherwise two consumers hold one device and,
 * worse, the bars stay wired to the idle stream for the whole capture. That
 * matters because both emit the same app-wide `AUDIO_LEVELS` event, so the bars
 * cannot tell which one is feeding them: whichever is running wins. An idle
 * stream left running through a capture is bound to whichever device was
 * selected before it began, and dies with that device — freezing the bars on
 * their last frame while the capture happily continues on another microphone.
 *
 * Both halves are tolerant of a stream that is not running: the handover is
 * best-effort decoration around a capture whose success must never depend on it.
 */

import { pTimeout } from '@/lib/utils/pTimeout';
import { logger } from '@/utils/logger/logger';

import { recordingLifecycle } from '../events/recording-lifecycle';
import { desktopAudioRecorderBridge } from './index';

const IDLE_STREAM_STOP_TIMEOUT_MS = 1_500;

/**
 * Release the microphone from the idle visualizer so a capture can claim it.
 *
 * Awaits the bridge call rather than only emitting the lifecycle event: the
 * event drives `useDesktopIdleStream`'s React state, which settles a tick later,
 * while the direct call releases the device now. A capture that opens the mic
 * before the release lands is the failure this ordering exists to prevent.
 */
export async function releaseIdleStreamForCapture(sessionId?: string): Promise<void> {
  // Tells every mounted consumer to stand down (DesktopIdleBars owns its state).
  void recordingLifecycle.emitStarting(sessionId);

  try {
    await pTimeout(
      desktopAudioRecorderBridge.stopIdleStream(),
      IDLE_STREAM_STOP_TIMEOUT_MS
    );
  } catch (error) {
    // Not an error path: the stream is routinely already stopped (a capture
    // starting while the window was blurred, a second start in the same tick),
    // or native teardown exceeded the bounded wait.
    logger.debug('[idle-stream-handover] No idle stream to stop', { error });
  }
}

/**
 * Give the microphone back to the idle visualizer once a capture has ended.
 *
 * Event-only on purpose. `useDesktopIdleStream` restarts itself on
 * `recording-stopped` after a delay that lets the OS release the device, and it
 * re-resolves the selected input as it does — a direct start here would race
 * that and open the mic before the capture has finished letting go.
 */
export function restoreIdleStreamAfterCapture(sessionId?: string): void {
  void recordingLifecycle.emitStopped(sessionId);
}
